import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import Header from '../components/header';
import Footer from '../components/footer';
import './CounsellorDashboard.css';

const CounsellorDashboard = () => {
    const { user } = useContext(MyContext);
    const navigate = useNavigate();
    
    const [sessions, setSessions] = useState([]);
    const [newRequests, setNewRequests] = useState([]);
    const [unreadMessages, setUnreadMessages] = useState([]);
    const [activeSessions, setActiveSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [currentView, setCurrentView] = useState('dashboard'); // dashboard, chat
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [chatContext, setChatContext] = useState(null);

    // Fetch counsellor sessions
    const fetchCounsellorSessions = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_counsellor_sessions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    counsellor_id: user.user_id
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setSessions(data.data.sessions || []);
                setNewRequests(data.data.new_requests || []);
                setUnreadMessages(data.data.unread_messages || []);
                setActiveSessions(data.data.active_sessions || []);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch sessions');
        } finally {
            setLoading(false);
        }
    };

    // Load a conversation
    const loadConversation = async (session_id, context = null) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_counselling_messages.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    conversation_id: session_id, 
                    user_id: user.user_id 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setSelectedSession(data.data.conversation);
                setChatContext(context);
                setMessages(data.data.messages || []);
                setCurrentView('chat');
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to load conversation');
        } finally {
            setLoading(false);
        }
    };

    // Send a message
    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedSession) return;

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/send_counselling_message.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversation_id: Number(selectedSession.id),
                    sender_id: Number(user.user_id),
                    message_text: newMessage.trim(),
                    message_type: 'text',
                    cloudinary_url: '',
                    file_name: '',
                    file_size: 0,
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setNewMessage('');
                // Reload messages
                await loadConversation(selectedSession.id, chatContext);
                // Refresh sessions list
                await fetchCounsellorSessions();
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to send message');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user && user.user_id) {
            fetchCounsellorSessions();
        }
    }, [user]);

    const renderDashboard = () => (
        <div className="container-fluid p-3">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex align-items-center mb-4">
                        <div>
                            <h2 className="mb-0">👨‍⚕️ Counsellor Dashboard</h2>
                            <p className="text-muted mb-0">Manage your counselling sessions and chat requests</p>
                        </div>
                    </div>

                    {/* Statistics Cards */}
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="card bg-warning text-white">
                                <div className="card-body text-center">
                                    <h3>{newRequests.length}</h3>
                                    <p className="mb-0">New Requests</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-danger text-white">
                                <div className="card-body text-center">
                                    <h3>{unreadMessages.length}</h3>
                                    <p className="mb-0">Unread Messages</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-success text-white">
                                <div className="card-body text-center">
                                    <h3>{activeSessions.length}</h3>
                                    <p className="mb-0">Active Sessions</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-info text-white">
                                <div className="card-body text-center">
                                    <h3>{sessions.length}</h3>
                                    <p className="mb-0">Total Sessions</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* New Requests */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header bg-warning text-white">
                                    <h5 className="mb-0">🆕 New Chat Requests</h5>
                                </div>
                                <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {newRequests.length > 0 ? (
                                        <div className="list-group list-group-flush">
                                            {newRequests.map(session => (
                                                <div 
                                                    key={session.session_id} 
                                                    className="list-group-item list-group-item-action"
                                                    onClick={() => loadConversation(session.session_id, { groupName: session.group?.name })}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className="me-3">
                                                            {session.member.photo ? (
                                                                <img 
                                                                    src={session.member.photo} 
                                                                    alt={session.member.name}
                                                                    className="rounded-circle"
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <span>👤</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{session.member.name}</h6>
                                                            <small className="text-muted">{session.group.name}</small>
                                                            <br />
                                                            <small className="text-warning">New request - Click to start chat</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted text-center">No new requests</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Unread Messages */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header bg-danger text-white">
                                    <h5 className="mb-0">📩 Unread Messages</h5>
                                </div>
                                <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {unreadMessages.length > 0 ? (
                                        <div className="list-group list-group-flush">
                                            {unreadMessages.map(session => (
                                                <div 
                                                    key={session.session_id} 
                                                    className="list-group-item list-group-item-action"
                                                    onClick={() => loadConversation(session.session_id, { groupName: session.group?.name })}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className="me-3">
                                                            {session.member.photo ? (
                                                                <img 
                                                                    src={session.member.photo} 
                                                                    alt={session.member.name}
                                                                    className="rounded-circle"
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <span>👤</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{session.member.name}</h6>
                                                            <small className="text-muted">{session.group.name}</small>
                                                            {session.last_message && (
                                                                <p className="mb-1 text-truncate" style={{ maxWidth: '200px' }}>
                                                                    {session.last_message.text}
                                                                </p>
                                                            )}
                                                            <span className="badge bg-danger">{session.unread_count} unread</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted text-center">No unread messages</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Active Sessions */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header bg-success text-white">
                                    <h5 className="mb-0">💬 Active Sessions</h5>
                                </div>
                                <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {activeSessions.length > 0 ? (
                                        <div className="list-group list-group-flush">
                                            {activeSessions.map(session => (
                                                <div 
                                                    key={session.session_id} 
                                                    className="list-group-item list-group-item-action"
                                                    onClick={() => loadConversation(session.session_id, { groupName: session.group?.name })}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className="me-3">
                                                            {session.member.photo ? (
                                                                <img 
                                                                    src={session.member.photo} 
                                                                    alt={session.member.name}
                                                                    className="rounded-circle"
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <span>👤</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{session.member.name}</h6>
                                                            <small className="text-muted">{session.group.name}</small>
                                                            {session.last_message && (
                                                                <p className="mb-1 text-truncate" style={{ maxWidth: '200px' }}>
                                                                    {session.last_message.text}
                                                                </p>
                                                            )}
                                                            <small className="text-muted">
                                                                {session.last_message_at ? 
                                                                    new Date(session.last_message_at).toLocaleDateString() : 
                                                                    new Date(session.created_at).toLocaleDateString()
                                                                }
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted text-center">No active sessions</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderChatView = () => (
        <div className="container-fluid p-0 h-100">
            <div className="d-flex flex-column h-100">
                {/* Chat Header */}
                <div className="bg-white border-bottom p-3">
                    <div className="d-flex align-items-center">
                        <button 
                            className="btn btn-outline-secondary me-3"
                            onClick={() => {
                                setChatContext(null);
                                setCurrentView('dashboard');
                            }}
                        >
                            ← Back to Dashboard
                        </button>
                        <div className="d-flex align-items-center">
                            <div className="me-3">
                                <div 
                                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                    style={{ width: '40px', height: '40px' }}
                                >
                                    <span>🕵️</span>
                                </div>
                            </div>
                            <div>
                                <h6 className="mb-0">Anonymous member</h6>
                                <small className="text-muted">{chatContext?.groupName || 'Counselling'}</small>
                                <div className="small text-muted">You do not see this person&apos;s name. Replies remain confidential.</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chat Messages */}
                <div className="flex-grow-1 p-3" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    {messages.map(message => {
                        const isOwn = message.sender && message.sender.id != null && String(message.sender.id) === String(user.user_id);
                        return (
                        <div key={message.id} className={`mb-3 ${isOwn ? 'text-end' : 'text-start'}`}>
                            <div className={`d-inline-block p-3 rounded ${isOwn ? 'bg-primary text-white' : 'bg-light'}`} style={{ maxWidth: '70%' }}>
                                <p className="mb-1">{message.message_text}</p>
                                <small className={isOwn ? 'text-white-50' : 'text-muted'}>
                                    {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </small>
                            </div>
                        </div>
                    );})}
                </div>

                {/* Chat Input */}
                <div className="bg-white border-top p-3">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your response..."
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                            disabled={loading}
                        />
                        <button 
                            className="btn btn-primary"
                            onClick={sendMessage} 
                            disabled={loading || !newMessage.trim()}
                        >
                            {loading ? '⏳' : '📤'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!user || !user.user_id) {
        return <div className="container-fluid p-3">
            <div className="alert alert-warning" role="alert">
                <h5>Authentication Required</h5>
                <p>Please log in to access counsellor dashboard.</p>
            </div>
        </div>;
    }

    if (loading && currentView === 'dashboard') {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading counsellor dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
                <button className="btn btn-primary" onClick={() => navigate('/groups')}>
                    Back to Groups
                </button>
            </div>
        );
    }

    return (
        <div className="h-100 d-flex flex-column">
            <Header />
            <div className="flex-grow-1">
                {currentView === 'dashboard' && renderDashboard()}
                {currentView === 'chat' && renderChatView()}
            </div>
            <Footer />
        </div>
    );
};

export default CounsellorDashboard;
